"use client";
import { useRef, useState, type ReactNode } from "react";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import type { ImageType, LinkType } from "@/types/baseTypes";
import type { ExtendedLinkType } from "@/components/ExtededLink/ExtendedLink";
import useScrollLock from "@/hooks/useScrollLock";
import useIsOverLightSection from "@/hooks/useIsOverLightSection";
import { ContactBubbleRow } from "./ContactBubbleRow";
import styles from "./ContactBubble.module.scss";

type ContactBubbleElementBase = {
  icon: ImageType;
};

export type ContactBubbleElement =
  | (ContactBubbleElementBase & {
      type: "booking";
      bookingLink: Exclude<LinkType, null | "" | false>;
    })
  | (ContactBubbleElementBase & {
      type: "link";
      linkExtended: ExtendedLinkType;
    })
  | (ContactBubbleElementBase & {
      type: "modal";
      modalContent: ReactNode;
    });

interface ContactBubbleClientProps {
  title: string;
  elements: ContactBubbleElement[];
  triggerLabel: string;
  closeLabel: string;
  backLabel: string;
}

const CONTACT_BUBBLE_TITLE_ID = "contact-bubble-title";

export const ContactBubbleClient = ({
  title,
  elements,
  triggerLabel,
  closeLabel,
  backLabel,
}: ContactBubbleClientProps) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const isOverLightSection = useIsOverLightSection(triggerRef, !open);

  useScrollLock(open);

  const close = () => {
    setOpen(false);
    setActiveIndex(null);
  };

  const activeElement = activeIndex !== null ? elements[activeIndex] : null;
  const isFormActive = activeElement?.type === "modal";

  return (
    <>
      {!open && (
        <button
          ref={triggerRef}
          type="button"
          className={removeExtraSpacesFromClassNames(
            `${styles["trigger"]} ${isOverLightSection ? styles["is-on-light"] : ""}`,
          )}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-label={triggerLabel}
        >
          <CustomSVG
            iconKey="contact"
            wrapper="span"
            className={styles["trigger-icon"]}
            width={22}
            height={20}
            aria-hidden={true}
          />
          <span className={styles["trigger-label"]} aria-hidden={true}>
            {triggerLabel}
          </span>
        </button>
      )}

      {open && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-outside-to-close, dialog itself is keyboard/focus-trap operable
        <div
          className={removeExtraSpacesFromClassNames(
            `${styles["backdrop"]} ${isFormActive ? styles["is-form"] : ""}`,
          )}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dialog on open */}
          <FocusTrap autoFocus>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={CONTACT_BUBBLE_TITLE_ID}
              className={styles["dialog"]}
              onKeyDown={(event) => {
                if (event.key === "Escape") close();
              }}
            >
              <div
                className={removeExtraSpacesFromClassNames(
                  `${styles["panel"]} ${isFormActive ? styles["form-panel"] : ""}`,
                )}
                data-lenis-prevent
              >
                {activeElement?.type === "modal" ? (
                  <>
                    <div className={styles["modal-content"]} data-lenis-prevent>
                      {activeElement.modalContent}
                    </div>
                    <div className={styles["back-bar"]}>
                      <button
                        type="button"
                        className={styles["back-btn"]}
                        onClick={() => setActiveIndex(null)}
                      >
                        <CustomSVG
                          iconKey="arrowLeft"
                          wrapper="span"
                          width={20}
                          height={20}
                          aria-hidden={true}
                        />
                        {backLabel}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p id={CONTACT_BUBBLE_TITLE_ID} className={styles["title"]}>
                      {title}
                    </p>
                    <ul className={styles["list"]}>
                      {elements.map((element, index) => (
                        <li key={index} className={styles["row"]}>
                          <ContactBubbleRow
                            element={element}
                            onOpenModal={() => setActiveIndex(index)}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <button
                type="button"
                className={styles["close-btn"]}
                onClick={close}
              >
                <CustomSVG
                  iconKey="close"
                  wrapper="span"
                  width={13}
                  height={14}
                  aria-hidden={true}
                  className={styles["close-icon"]}
                />
                <span>{closeLabel}</span>
              </button>
            </div>
          </FocusTrap>
        </div>
      )}
    </>
  );
};
